import { useContext } from 'react';
import { createStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { logger } from '@/lib/logger';

import { SUPPORTED_CHAINS } from '../../constants/chain';
import { TokenResult } from '../../utils/0x/token-registry.types';
import { normalizeTokenAddress } from '../../utils/normalizeAddress';
import { TokenStoreContext } from './provider';

// Note: This interface can be expanded to cache other relevant application level data (balance, etc)
interface TokenDetails {
  token: TokenResult;
}

export interface TokenAddressMap {
  [chainId: number]: {
    [tokenAddress: string]: TokenDetails;
  };
}

export interface TokenState {
  tokens: TokenAddressMap;
}

export interface TokenStore extends TokenState {
  addTokens: (tokenResults: TokenResult[]) => void;
}

export const DEFAULT_STATE: TokenState = {
  tokens: SUPPORTED_CHAINS.reduce(
    (memo, chain) => {
      memo[chain.id] = {};
      return memo;
    },
    {} as { [chainId: number]: Record<string, TokenDetails> },
  ),
};

export const createTokenStore = (initialState: TokenState = DEFAULT_STATE) => {
  return createStore<TokenStore>()((set, get) => ({
    ...initialState,
    addTokens: (tokenResults: TokenResult[]) => {
      try {
        const store = get();
        const newTokens = tokenResults.reduce(
          (obj, token: TokenResult) => {
            const chainId = token.chainId;
            const key = normalizeTokenAddress(token.address, chainId);
            obj[chainId] = {
              ...obj[chainId],
              [key]: {
                token: {
                  ...(store.tokens[chainId]?.[key]?.token || {}),
                  ...token,
                },
              },
            };

            return obj;
          },
          { ...store.tokens },
        );

        set({
          tokens: newTokens,
        });
      } catch (err: unknown) {
        logger.debug('token store addTokens error:', err);
      }
    },
  }));
};

export const useTokenStore = <T>(selector: (store: TokenStore) => T): T => {
  const tokenStoreContext = useContext(TokenStoreContext);

  if (!tokenStoreContext) {
    throw new Error(`useTokenStore must be used within TokenStoreProvider`);
  }

  return useStoreWithEqualityFn(tokenStoreContext, selector, shallow);
};

export const tokensSelector = (state: TokenStore) => ({
  tokens: state.tokens,
  addTokens: state.addTokens,
});
